import React, { useEffect, useState } from "react";
import {
  calculatePresentValue,
  calculateFutureValue,
  calculatePeriodicPayment,
  rmdDistributionPeriods,
  stateIncomeTaxRates,
  calculateFederalIncomeTax,
  allocateSavings,
} from "./retirementGoalCalculations";
import { calculateTotalTax } from "./taxCalculations";
import { formatCurrencyAmount } from "./formatAmount";
import WithdrawalStrategyChart from "./WithdrawalStrategyChart";

export default function RetirementProjection({
  payload = {},
  taxInfo,
  taxInfoAllData,
  rateOfReturnData,
  setPayload,
}) {
  const [yearsUntilRetirement, setYearsUntilRetirement] = useState(0);
  const [netAfterTax, setNetAfterTax] = useState(0);
  const [savingsAllocationMonthly, setSavingsAllocationMonthly] = useState({
    deferred: 0,
    free: 0,
    taxable: 0,
  });
  const [userEstimatedValue, setUserEstimatedValue] = useState(0);
  const [goalsValue, setGoalsValue] = useState(0);
  const [userShortfall, setUserShortfall] = useState(0);
  const [userMonthly, setUserMonthly] = useState(0);

  const [projectionTable, setProjectionTable] = useState([]);
  const [proposedMonthlySavings, setProposedMonthlySavings] = useState(null);

  const getNum = (key, fallback = 0) => {
    const v = payload && payload[key];
    if (v === undefined || v === null || v === "") return fallback;
    return typeof v === "number"
      ? v
      : parseFloat(String(v).replace(/[^0-9.\-]/g, "")) || fallback;
  };

  function getSuggestedSavings(targetAccount, allocationMonthly) {
    if (!targetAccount) return 0;

    if (targetAccount.includes("Tax-Deferred"))
      return allocationMonthly.deferred;
    if (targetAccount.includes("Roth")) return allocationMonthly.free;
    if (targetAccount.includes("Brokerage")) return allocationMonthly.taxable;

    return 0;
  }

  function hasChanges(oldPayload, updates) {
    return Object.keys(updates).some((key) => {
      const oldVal = oldPayload[key];
      const newVal = updates[key];
      return String(oldVal) !== String(newVal);
    });
  }

  useEffect(() => {
    if (!payload || !taxInfoAllData) return;

    // ----------------------------
    // SAFE GETTER (NO ?? USED)
    // ----------------------------
    function getVal(key, fallback) {
      const v = payload && payload[key];
      return v !== undefined && v !== null && v !== "" ? Number(v) : fallback;
    }

    // === Extract Inputs ===
    const currentAge = getVal("Current Age", 0);
    const desiredRetAge = getVal("Desired Retirement Age", 0);
    const lifeExpectancy = getVal("Client Life Expectancy", 90);

    const annualPostRetBudget = getVal("Net Annual Post-Retirement Budget", 0);

    // let inflRaw = payload["Inflation rate for this goal"];
    // let inflationRate = 0;
    // if (inflRaw !== undefined && inflRaw !== null && inflRaw !== "") {
    //   inflRaw = String(inflRaw).replace(/[^0-9.\-]/g, "");
    //   inflationRate = parseFloat(inflRaw);
    //   if (inflationRate > 1) inflationRate = inflationRate / 100;
    // }
    const inflationInput = payload && payload["Inflation rate for this goal"];
    const matchedInflation =
      taxInfo && taxInfo.find((item) => item.name === inflationInput);
    const inflationRate = matchedInflation
      ? (parseFloat(matchedInflation.value) || 0) / 100
      : 0;

    const returnRate = (payload && payload["Assumed Annual Investment Return (%)"]) || (rateOfReturnData && rateOfReturnData.staticPercentage);

    const annualReturnRate =
      returnRate !== undefined && returnRate !== null
        ? Number(returnRate) / 100
        : 0.05;
    // console.log("Annual Return Rate:", annualReturnRate,returnRate);
    const ssUser = getVal("Your Estimated Social Security", 0);
    const ssSpouse = getVal("Social Security - Spouse", 0);
    const pension = getVal("Pension", 0);
    const rental = getVal("Net Rental Income", 0);

    let additionalIncomes = [];
    if (Array.isArray(payload["Annual Amount"])) {
      additionalIncomes = payload["Annual Amount"];
    } else if (payload["Annual Amount"]) {
      additionalIncomes = [payload["Annual Amount"]];
    }

    const sumAdditionalIncome = additionalIncomes.reduce(function (s, v) {
      return s + (parseFloat(v) || 0);
    }, 0);

    const taxableBalance = getVal("Taxable Accounts Balance (Brokerage, Savings)", 0);
    const taxDeferredBalance = getVal("Tax-Deferred Accounts Balance (Traditional 401k/IRA)", 0);
    const taxDeferredYearEndBalance = getVal("Tax-Deferred Accounts Prior Year-End Balance (for RMD if age 73+)", 0);
    const taxFreeBalance = getVal("Tax-Free Accounts Balance (Roth 401k/IRA)", 0);

    const taxableContrib = getVal("Current Taxable Accounts Annual Contribution", 0);
    const taxDeferredContrib = getVal("Current Tax-Deferred Accounts Annual Contribution", 0);
    const taxFreeContrib = getVal("Current Tax-Free Accounts Annual Contribution", 0);

    const spouseAge = getVal("Spouse's Current Age", 0);

    const filingStatus = payload["Tax Filing Status"] || "Single";

    const state =
      payload["State of Residence in Retirement"] ||
      payload["state"] ||
      "California";

    const yearsToRetirement = Math.max(0, desiredRetAge - currentAge);
    const yearsInRetirement = Math.max(0, lifeExpectancy - desiredRetAge);

    // ----------------------------
    // PROJECTED VALUE UNTIL RETIREMENT
    // ----------------------------
    const currentTotalBalance =
      taxableBalance + taxDeferredBalance + taxFreeBalance;
    const currentTotalContrib =
      taxableContrib + taxDeferredContrib + taxFreeContrib;

    const projectedValue = calculateFutureValue(
      annualReturnRate,
      yearsToRetirement,
      -currentTotalContrib,
      -currentTotalBalance
    );

    // ----------------------------
    // SAVINGS GOAL IN TODAYS DOLLARS
    // ----------------------------
    const realRateOfReturn =
      (annualReturnRate - inflationRate) / (1 + inflationRate);

    const totalSavingsGoalAtRetirementInTodaysDollars = calculatePresentValue(
      realRateOfReturn,
      yearsInRetirement,
      -annualPostRetBudget,
      0
    );

    const totalSavingsGoalNominal =
      totalSavingsGoalAtRetirementInTodaysDollars *
      Math.pow(1 + inflationRate, yearsToRetirement);

    const finalShortfall = Math.max(
      0,
      totalSavingsGoalNominal - projectedValue
    );

    const requiredMonthly =
      finalShortfall > 0
        ? Math.max(
            0,
            -calculatePeriodicPayment(
              annualReturnRate / 12,
              Math.max(1, yearsToRetirement * 12),
              0,
              finalShortfall
            )
          )
        : 0;

    // ----------------------------
    // SET BASE VALUES
    // ----------------------------
    const userProposedMonthlyFromPayload = getNum("Your Commitment", NaN);
    // FIXED proposedMonthly
    let proposedMonthly = 0;

    if (
      userProposedMonthlyFromPayload !== undefined &&
      userProposedMonthlyFromPayload !== null &&
      userProposedMonthlyFromPayload !== "" &&
      !isNaN(Number(userProposedMonthlyFromPayload))
    ) {
      proposedMonthly = Number(userProposedMonthlyFromPayload);
    } else {
      proposedMonthly = requiredMonthly;
    }

    // FIXED allocationMonthly
    const allocationMonthly = allocateSavings(proposedMonthly);
    // console.log(
    //   "Allocation Monthly:",
    //   payload["Target Account"],
    //   proposedMonthly,
    //   allocationMonthly,
    //   userProposedMonthlyFromPayload,
    //   requiredMonthly
    // );

    // Determine Suggested Savings for chosen account
    const targetAccount = payload["Target Account"];
    const suggestedSavings = getSuggestedSavings(
      targetAccount,
      allocationMonthly
    );

    // Remaining gap logic
    const remainingGap = Math.max(0, requiredMonthly - proposedMonthly);

    console.log("projjjejeje", projectedValue);
    setSavingsAllocationMonthly(allocationMonthly);
    setYearsUntilRetirement(yearsToRetirement);
    setUserEstimatedValue(projectedValue);
    setGoalsValue(totalSavingsGoalNominal);
    setUserShortfall(finalShortfall);
    setUserMonthly(requiredMonthly);

    // -------------------------------------------------------
    // RETIREMENT SPENDING PROJECTION TABLE
    // -------------------------------------------------------
    function runFullProjection() {
      // Grow each account to retirement (future value)
      let taxableStartBal = calculateFutureValue(
        annualReturnRate,
        yearsToRetirement,
        -taxableContrib,
        -taxableBalance
      );
      let taxDeferredStartBal = calculateFutureValue(
        annualReturnRate,
        yearsToRetirement,
        -taxDeferredContrib,
        -taxDeferredBalance
      );
      let taxFreeStartBal = calculateFutureValue(
        annualReturnRate,
        yearsToRetirement,
        -taxFreeContrib,
        -taxFreeBalance
      );

      // Input Object for calculateTotalTax
      const itemized =
        taxInfoAllData && taxInfoAllData.deductions === "itemized"
          ? {
              medical:
                (taxInfoAllData &&
                  parseFloat(taxInfoAllData["Medical and Dental Expenses"])) ||
                0,
              salt:
                (taxInfoAllData &&
                  parseFloat(taxInfoAllData["State and Local Taxes (SALT)"])) ||
                0,
              mortgage:
                (taxInfoAllData &&
                  parseFloat(taxInfoAllData["Home Mortgage Interest"])) ||
                0,
              charity:
                (taxInfoAllData &&
                  parseFloat(taxInfoAllData["Charitable Contributions"])) ||
                0,
            }
          : {};
      const taxInputs = {
        w2Income: (taxInfoAllData && Number(taxInfoAllData.grossIncome)) || 0,
        seNetIncome:
          (taxInfoAllData && Number(taxInfoAllData.earnedIncome)) || 0, // Add if you have self-employment
        filingStatus: taxInfoAllData && taxInfoAllData.taxFillingElection,
        state:
          (payload && payload["State of Residence in Retirement"]) ||
          (taxInfoAllData && taxInfoAllData.state),
        city: taxInfoAllData && taxInfoAllData.city,
        hsa: (taxInfoAllData && Number(taxInfoAllData.hsaContributions)) || 0,
        studentLoanInterest:
          (taxInfoAllData && Number(taxInfoAllData.studentLoan)) || 0,
        tradIra:
          (taxInfoAllData && Number(taxInfoAllData.iraContribution)) || 0,
        sepSimpleContributions: 0,
        educator:
          (taxInfoAllData && Number(taxInfoAllData.educatorExpenses)) || 0,
        dependentCareExp:
          (taxInfoAllData && Number(taxInfoAllData.careExpenses)) || 0,
        educationExp:
          (taxInfoAllData && Number(taxInfoAllData.educationExpenses)) || 0,
        dependents:
          (taxInfoAllData && Number(taxInfoAllData.noOfDependents)) || 0,
        deductionType: taxInfoAllData && taxInfoAllData.deductions,
        itemized,
        // Capital Gains (net values passed directly)
        netShortTermCG:
          taxInfoAllData && taxInfoAllData.shortTermGainsLoss
            ? Number(taxInfoAllData.shortTermGainsLoss)
            : 0,
        netLongTermCG:
          taxInfoAllData && taxInfoAllData.longTermGainsLoss
            ? Number(taxInfoAllData.longTermGainsLoss)
            : 0,
        carryforward:
          taxInfoAllData && taxInfoAllData.lossCarryforward
            ? Number(taxInfoAllData.lossCarryforward)
            : 0, // Negative = loss
      };

      //  Calculate Current Year Tax

      const rows = [];

      for (let y = 0; y < yearsInRetirement; y++) {
        const year = new Date().getFullYear() + yearsToRetirement + y;
        const age = desiredRetAge + y;

        // Spending need for this year
        const spendingNeed =
          annualPostRetBudget *
          Math.pow(1 + inflationRate, y + yearsToRetirement);

        const fixedIncomeTotal =
          (ssUser + ssSpouse + pension + rental + sumAdditionalIncome) *
          Math.pow(1 + inflationRate, y + yearsToRetirement);

        let portfolioWithdrawalNeed = Math.max(
          0,
          spendingNeed - fixedIncomeTotal
        );

        const taxableStart = taxableStartBal;
        const taxDeferredStart = taxDeferredStartBal;
        const taxFreeStart = taxFreeStartBal;

        // --------------------------------
        //  1) TAXABLE WITHDRAW
        // --------------------------------
        let taxableWithdraw = Math.min(portfolioWithdrawalNeed, taxableStart);
        let remainingNeed = portfolioWithdrawalNeed - taxableWithdraw;

        // --------------------------------
        //  2) RMD (Tax‐Deferred)
        // --------------------------------
        let rmd = 0;
        const rmdPeriod = rmdDistributionPeriods[age];
        if (age >= 73 && taxDeferredStart > 0 && rmdPeriod) {
          rmd = taxDeferredStart / rmdPeriod;
        }

        let taxDeferredWithdraw = Math.max(
          rmd,
          Math.min(remainingNeed, taxDeferredStart)
        );
        remainingNeed -= Math.min(taxDeferredWithdraw, remainingNeed);

        // --------------------------------
        //  3) ROTH WITHDRAW
        // --------------------------------
        let rothWithdraw = Math.min(remainingNeed, taxFreeStart);
        remainingNeed -= rothWithdraw;

        // --------------------------------
        //  GROWTH
        // --------------------------------
        const taxableGrowth =
          (taxableStart - taxableWithdraw) * annualReturnRate;
        const taxDeferredGrowth =
          (taxDeferredStart - taxDeferredWithdraw) * annualReturnRate;
        const rothGrowth = (taxFreeStart - rothWithdraw) * annualReturnRate;

        const taxableEnd = Math.max(
          0,
          taxableStart - taxableWithdraw + taxableGrowth
        );
        const taxDeferredEnd = Math.max(
          0,
          taxDeferredStart - taxDeferredWithdraw + taxDeferredGrowth
        );
        const rothEnd = Math.max(0, taxFreeStart - rothWithdraw + rothGrowth);

        const totalWithdrawals =
          taxableWithdraw + taxDeferredWithdraw + rothWithdraw;

        const totalPreTaxIncome =
          fixedIncomeTotal + taxDeferredWithdraw + taxableWithdraw;

        // STATE TAX
        // const stateRate = stateIncomeTaxRates[state]
        //   ? stateIncomeTaxRates[state]
        //   : 0;
        // const stateTaxes = totalPreTaxIncome * stateRate;

        // const federalTaxes = calculateFederalIncomeTax(totalPreTaxIncome, {
        //   filingStatus: filingStatus,
        //   age: age,
        //   spouseAge: spouseAge,
        // });
        const taxInputsForYear = {
          ...taxInputs,
          w2Income: totalPreTaxIncome,
          filingStatus,
          age,
          spouseAge,
        };

        const yearTaxResult = calculateTotalTax(taxInputsForYear);

        const federalTaxes = (yearTaxResult && yearTaxResult.fedTax) || 0;
        const stateTaxes = (yearTaxResult && yearTaxResult.stateTax) || 0;

        const totalEndingBalance = taxableEnd + taxDeferredEnd + rothEnd;

        rows.push({
          year: year,
          age: age,
          retirementStatus: "Retired",
          annualSpendingNeed: spendingNeed,
          socialSecurityIncome:
            (ssUser + ssSpouse) *
            Math.pow(1 + inflationRate, y + yearsToRetirement),
          pensionIncome:
            pension * Math.pow(1 + inflationRate, y + yearsToRetirement),
          otherIncome:
            (rental + sumAdditionalIncome) *
            Math.pow(1 + inflationRate, y + yearsToRetirement),
          portfolioWithdrawalNeed: portfolioWithdrawalNeed,
          taxableStart: taxableStart,
          taxableGrowth: taxableGrowth,
          taxableWithdrawals: taxableWithdraw,
          taxableEnd: taxableEnd,
          taxDeferredStart: taxDeferredStart,
          taxDeferredGrowth: taxDeferredGrowth,
          rmdCalculation: rmd,
          taxDeferredWithdrawals: taxDeferredWithdraw,
          taxDeferredEnd: taxDeferredEnd,
          rothStart: taxFreeStart,
          rothGrowth: rothGrowth,
          rothWithdrawals: rothWithdraw,
          rothEnd: rothEnd,
          totalWithdrawals: totalWithdrawals,
          totalPreTaxIncome: totalPreTaxIncome,
          estimatedStateTaxesPaid: stateTaxes,
          estimatedFederalTaxesPaid: federalTaxes,
          totalEndingBalance: totalEndingBalance,
        });

        // prepare balances for next year
        taxableStartBal = taxableEnd;
        taxDeferredStartBal = taxDeferredEnd;
        taxFreeStartBal = rothEnd;
      }

      return rows;
    }

    const projectionRows = runFullProjection();

    const lastBalanceRow = projectionRows[projectionRows.length - 1];
    let percentFunded = 0;

    // if (lastBalanceRow && lastBalanceRow.totalEndingBalance > 0) {
    //   // Portfolio never hit 0 — reached life expectancy
    //   percentFunded = 100;
    // } else if (lastBalanceRow) {
    //   const ageReached = lastBalanceRow.age;
    //   const numerator = ageReached - desiredRetAge;
    //   const denominator = lifeExpectancy - desiredRetAge;

    //   percentFunded = Math.max(0, Math.min(100, (numerator / denominator) * 100));
    // }

    if (proposedMonthly >= requiredMonthly) {
      percentFunded = 100; // Fully funded
    } else {
      percentFunded = (proposedMonthly / requiredMonthly) * 100;
    }

    // Final display value (rounded to whole percent)
    const fundsLastUntilAge = `${percentFunded.toFixed(2)}`;

    // NEW PAYLOAD VALUES
    const updatedValues = {
      "Current Monthly Gap": requiredMonthly,
      "Suggested Savings": suggestedSavings,
      // "Your Commitment": proposedMonthly,
      "Remaining Monthly Gap": remainingGap,
      "Funds Last Until Age": fundsLastUntilAge,
    };
    // console.log("Updated Values:", updatedValues,lastBalanceRow,projectionRows);
    // ONLY SET PAYLOAD IF SOMETHING CHANGED
    if (hasChanges(payload, updatedValues)) {
      setPayload((prev) => ({ ...prev, ...updatedValues }));
    }
    setProjectionTable(projectionRows);
  }, [payload, taxInfoAllData]);

  return (
    <div className="ret-projection">
      <h2 className="ret-title">Retirement Plan Results</h2>

      {/* Summary Section */}
      <div className="ret-summary">
        <div className="summary-grid">
          <div className="summary-item">
            <label>Total Savings Goal at Retirement:</label>
            <strong>{formatCurrencyAmount(goalsValue)}</strong>
          </div>

          <div className="summary-item">
            <label>Projected Value (with current savings):</label>
            <strong>{formatCurrencyAmount(userEstimatedValue)}</strong>
          </div>

          <div className="summary-item highlight">
            <label>Required Additional Monthly Savings:</label>
            <strong>{formatCurrencyAmount(userMonthly)}</strong>
          </div>

          <div className="summary-item">
            <label>Your Proposed Monthly Savings:</label>
            <strong>
              {formatCurrencyAmount(proposedMonthlySavings || userMonthly)}
            </strong>
          </div>

          <div className="summary-item full-width">
            <label>Suggested Allocation (monthly):</label>
            <div className="allocation-box">
              Tax-Deferred:{" "}
              <strong>
                {formatCurrencyAmount(savingsAllocationMonthly.deferred)}
              </strong>{" "}
              / mo
              <br />
              Tax-Free:{" "}
              <strong>
                {formatCurrencyAmount(savingsAllocationMonthly.free)}
              </strong>{" "}
              / mo
              <br />
              Taxable:{" "}
              <strong>
                {formatCurrencyAmount(savingsAllocationMonthly.taxable)}
              </strong>{" "}
              / mo
            </div>
          </div>

          <div className="summary-item">
            <label>Plan Net After Tax (annual):</label>
            <strong className="text-success">
              {formatCurrencyAmount(netAfterTax)}
            </strong>
          </div>
        </div>
      </div>

      <hr className="divider" />

      <h3 className="section-title">Annual Projection (Based on Your Plan)</h3>

      {/* Table */}
      <div className="table-container">
        <table className="projection-table">
          <thead>
            <tr>
              {[
                "Year",
                "Age",
                "Retirement Status",
                "Annual Spending Need (Inflated)",
                "Social Security Income",
                "Pension Income",
                "Other Income (Rental, etc.)",
                "Portfolio Withdrawal Need",
                "Taxable Acct: Start Balance",
                "Taxable Acct: Growth",
                "Taxable Acct: Withdrawals",
                "Taxable Acct: End Balance",
                "Tax-Deferred Acct: Start Balance",
                "Tax-Deferred Acct: Growth",
                "RMD Calculation",
                "Tax-Deferred Acct: Withdrawals",
                "Tax-Deferred Acct: End Balance",
                "Roth Acct: Start Balance",
                "Roth Acct: Growth",
                "Roth Acct: Withdrawals",
                "Roth Acct: End Balance",
                "Total Withdrawals From All Accounts",
                "Total Pre-Tax Income",
                "Estimated State Taxes Paid",
                "Estimated Federal Taxes Paid",
                "Total Ending Balance (All Accounts)",
              ].map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {projectionTable.map((r, i) => (
              <tr key={i}>
                <td className="text-center">{r.year}</td>
                <td className="text-center">{r.age}</td>
                <td className="text-center status">{r.retirementStatus}</td>
                <td className="text-center">
                  {formatCurrencyAmount(r.annualSpendingNeed)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.socialSecurityIncome)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.pensionIncome)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.otherIncome)}
                </td>
                <td className="text-center text-bold text-danger">
                  {formatCurrencyAmount(r.portfolioWithdrawalNeed)}
                </td>

                {/* Taxable */}
                <td className="text-center">
                  {formatCurrencyAmount(r.taxableStart)}
                </td>
                <td className="text-center text-success">
                  {formatCurrencyAmount(r.taxableGrowth)}
                </td>
                <td className="text-center text-danger">
                  {formatCurrencyAmount(r.taxableWithdrawals)}
                </td>
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.taxableEnd)}
                </td>

                {/* Tax-Deferred */}
                <td className="text-center">
                  {formatCurrencyAmount(r.taxDeferredStart)}
                </td>
                <td className="text-center text-success">
                  {formatCurrencyAmount(r.taxDeferredGrowth)}
                </td>
                <td className="text-center">
                  {r.rmdCalculation > 0
                    ? formatCurrencyAmount(r.rmdCalculation)
                    : "-"}
                </td>
                <td className="text-center text-danger">
                  {formatCurrencyAmount(r.taxDeferredWithdrawals)}
                </td>
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.taxDeferredEnd)}
                </td>

                {/* Roth */}
                <td className="text-center">
                  {formatCurrencyAmount(r.rothStart)}
                </td>
                <td className="text-center text-success">
                  {formatCurrencyAmount(r.rothGrowth)}
                </td>
                <td className="text-center text-danger">
                  {formatCurrencyAmount(r.rothWithdrawals)}
                </td>
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.rothEnd)}
                </td>

                {/* Summary */}
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.totalWithdrawals)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.totalPreTaxIncome)}
                </td>
                <td className="text-center text-warning">
                  {formatCurrencyAmount(r.estimatedStateTaxesPaid)}
                </td>
                <td className="text-center text-warning">
                  {formatCurrencyAmount(r.estimatedFederalTaxesPaid)}
                </td>
                <td className="text-center text-bold text-large">
                  {formatCurrencyAmount(r.totalEndingBalance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <WithdrawalStrategyChart
        projectionTable={projectionTable && projectionTable}
      />
    </div>
  );
}
